/**
 * Text normalization utilities for consistent text comparison
 * NOTE: These functions are ONLY for comparison purposes, not for storing/displaying text
 */

/**
 * Normalizes text for comparison purposes only:
 * 1. Removing HTML tags
 * 2. Normalizing all whitespace (including line breaks) to single spaces
 * 3. Trimming the text
 * 4. Removing any non-visible characters
 *
 * IMPORTANT: Do not use this function for storing or displaying text as it
 * removes line breaks and other formatting. Use only for comparison.
 */
export const normalizeText = (text: string): string => {
  if (!text) return "";

  // Step 1: Remove HTML tags more thoroughly
  const withoutHtml = text.replace(/<[^>]*>/g, "");

  // Step 2: Replace all whitespace (including all line breaks) with a single space
  // This includes handling both \n and <br> style line breaks
  const normalizedWhitespace = withoutHtml
    .replace(/\n/g, " ") // Convert explicit newlines to spaces
    .replace(/\r/g, " ") // Convert carriage returns to spaces
    .replace(/\s+/g, " "); // Collapse all remaining whitespace

  // Step 3: Final trim to remove leading and trailing spaces
  return normalizedWhitespace.trim();
};

/**
 * Compare two strings and log detailed differences for debugging
 * Returns true if the normalized versions of the texts match
 */
export const compareTexts = (text1: string, text2: string, label1 = "Text 1", label2 = "Text 2"): boolean => {
  const normalized1 = normalizeText(text1);
  const normalized2 = normalizeText(text2);

  // Log the original texts for debugging, specifically highlighting line breaks
  console.log(`=== RAW TEXT INFO ===`);
  console.log(`${label1} (${text1.length} chars, ${text1.split("\n").length} lines)`);
  console.log(`${label1} raw: "${text1.substring(0, 100).replace(/\n/g, "\\n")}${text1.length > 100 ? "..." : ""}"`);
  console.log(`${label2} (${text2.length} chars, ${text2.split("\n").length} lines)`);
  console.log(`${label2} raw: "${text2.substring(0, 100).replace(/\n/g, "\\n")}${text2.length > 100 ? "..." : ""}"`);

  console.log(`=== NORMALIZED TEXT COMPARISON (${label1} vs ${label2}) ===`);
  console.log(`${label1} (${normalized1.length} chars): "${normalized1}"`);
  console.log(`${label2} (${normalized2.length} chars): "${normalized2}"`);

  const isEqual = normalized1 === normalized2;
  console.log(`Match result: ${isEqual ? "MATCH ✓" : "NO MATCH ✗"}`);

  if (!isEqual) {
    console.log("Character by character analysis:");
    let differenceFound = false;

    const maxLength = Math.max(normalized1.length, normalized2.length);
    for (let i = 0; i < maxLength; i++) {
      const char1 = i < normalized1.length ? normalized1[i] : "";
      const char2 = i < normalized2.length ? normalized2[i] : "";

      if (char1 !== char2) {
        differenceFound = true;
        const char1Code = char1 ? char1.charCodeAt(0) : "none";
        const char2Code = char2 ? char2.charCodeAt(0) : "none";
        console.log(`Difference at position ${i}: '${char1}' (${char1Code}) vs '${char2}' (${char2Code})`);

        // Show a bit of context around the difference
        const start = Math.max(0, i - 10);
        const end1 = Math.min(normalized1.length, i + 10);
        const end2 = Math.min(normalized2.length, i + 10);

        console.log(`${label1} context: "...${normalized1.substring(start, end1)}..."`);
        console.log(`${label2} context: "...${normalized2.substring(start, end2)}..."`);

        // Only show the first few differences to keep logs manageable
        if (i > 20) {
          console.log("Too many differences, stopping comparison");
          break;
        }
      }
    }

    if (!differenceFound && normalized1.length !== normalized2.length) {
      console.log(`Texts have different lengths: ${normalized1.length} vs ${normalized2.length}`);
    }
  }

  return isEqual;
};
